import { useEffect, useState } from 'react';
// import { FunctionalArea, Member, Team, TeamMember, TeamMemberRole } from '../../../types/team';
import { User } from '../../messaging/types';
import { groupApi } from '../group-api';
import { Group, GroupMember } from '../types';
import { toast } from 'react-toastify';

export function GroupCreate({ onClose, group, isCreate }: { onClose: () => void, group?: Group, isCreate: boolean }) {
    // const { theme } = useTheme();
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTeam, setSelectedTeam] = useState<Group | undefined | null>(group);
    const [isCreating, setIsCreating] = useState(isCreate);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState<Group>({
        name: group?.name || '',
        description: group?.name || '',
        members: [] as GroupMember[]
    });

    const [groupMembers, setGroupMembers] = useState<GroupMember[]>(group?.members || []);

    const [showUserModal, setShowUserModal] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [showMemberModal, setShowMemberModal] = useState(false);
    const [editingMember, setEditingMember] = useState<GroupMember | null>(null);


    const fetchUsers = async () => {
        try {
            const data = await groupApi.getAllUsers();
            setUsers(data);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        }
    };

    useEffect(() => {
        fetchUsers();
        // setGroupMembers(group?.members||[]);
    }, [])

    const handleCreateTeam = async () => {
        // Validation
        if (!formData.name || formData.name.trim() === '') {
            toast.error('Group name is required');
            setError('Group name is required');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const groupData = {
                ...formData,
                name: formData.name.trim(),
                description: formData.description?.trim() || '',
                memberIds: selectedUserIds
            };

            const response = await groupApi.createGroup(groupData);

            // Only show success if we got a valid response with group data
            if (response && (response.id || response.data?.id || response.data)) {
                toast.success('Group created successfully!');
                
                // Reset form
                setIsCreating(false);
                setFormData({
                    name: '',
                    description: '',
                    members: []
                });
                setGroupMembers([]);
                setSelectedUserIds([]);
                
                // Close modal
                onClose();
            } else {
                // Response received but no valid data
                const errorMessage = 'Group creation failed: Invalid response from server';
                setError(errorMessage);
                toast.error(errorMessage);
            }
        } catch (err: any) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to create group. Please try again.';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateTeam = async (id?: number) => {
        if (!selectedTeam) return;
        if (!id) return;

        // Validation
        if (!formData.name || formData.name.trim() === '') {
            toast.error('Group name is required');
            setError('Group name is required');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const teamData = {
                ...formData,
                name: formData.name.trim(),
                description: formData.description?.trim() || '',
                memberIds: selectedUserIds
            };

            const response = await groupApi.updateGroup(id, teamData);
            
            // Only show success if we got a valid response
            if (response && (response.id || response.data?.id || response.data)) {
                toast.success('Group updated successfully!');
                
                // Close modal
                onClose();
            } else {
                // Response received but no valid data
                const errorMessage = 'Group update failed: Invalid response from server';
                setError(errorMessage);
                toast.error(errorMessage);
            }
        } catch (err: any) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to update group. Please try again.';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFormChange = (field: keyof Group, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const availableUsers = users.filter(
        (u) => !groupMembers?.some(member => member.id === u.id)
    ).filter(u => u.name.toLowerCase().includes(userSearch.toLowerCase()));

    const handleOpenUserModal = () => {
        setUserSearch('');
        setSelectedUserIds([]);
        setShowUserModal(true);
    };

    const handleAddSelectedUsers = () => {

        const newMembers: GroupMember[] = selectedUserIds.map(userId => ({
            id: userId,
            name: ''
        }));
        setGroupMembers(prev => [...prev, ...newMembers]);
        setShowUserModal(false);
    };



    const handleRemoveMember = (userId: number) => {
        setGroupMembers(prev => prev.filter(m => m.id !== userId));
    };


    const getUserById = (userId: number) => {
        return users.find(u => u.id === userId);
    };



    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }



    return (
        <div className="fixed inset-0 z-50 flex justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full p-8 relative mt-8 mb-8 overflow-y-scroll">

                <div className="p-6">
                    <h2 className="text-xl font-semibold text-black dark:text-white mb-4">
                        {isCreating ? 'Create Group' : 'Edit Group  '}
                    </h2>
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                            <div className="flex items-center gap-2">
                                <i className="fa-solid fa-circle-exclamation text-red-600 dark:text-red-400"></i>
                                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                            </div>
                        </div>
                    )}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                Group Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => {
                                    handleFormChange('name', e.target.value);
                                    setError(null); // Clear error when user types
                                }}
                                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white ${
                                    error && !formData.name ? 'border-red-500 dark:border-red-500' : 'dark:border-neutral-700'
                                }`}
                                placeholder="Enter group name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                Description
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleFormChange('description', e.target.value)}
                                className="w-full px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
                                rows={3}
                            />
                        </div>

                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                                    Group Members
                                </label>
                                <button
                                    type="button"
                                    onClick={handleOpenUserModal}
                                    className="btn-primary px-3 py-1 text-sm"
                                >
                                    Add Member
                                </button>
                            </div>



                            {/* Team Members List */}
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {groupMembers?.map(member => {

                                    const user = getUserById(member.id);

                                    return (
                                        <div key={member.id} className="flex items-center justify-between p-2 border dark:border-neutral-700 rounded">
                                            <div className="flex items-center gap-2">
                                                <img
                                                    src={user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.name}`}
                                                    className="w-10 h-10 rounded-full border"
                                                    alt={user?.name}
                                                />
                                                <span className="text-black dark:text-white">{user?.name}</span>
                                                {user?.phone && (
                                                    <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                                        ({user.phone})
                                                    </span>
                                                )}
                                            </div>

                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="flex space-x-4 pt-4 justify-end">
                            <button
                                onClick={() => {
                                    setIsCreating(false);
                                    setSelectedTeam(null);
                                    setGroupMembers([]);
                                    setSelectedUserIds([]);
                                    setError(null);
                                    onClose();
                                }}
                                disabled={isSubmitting}
                                className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={isCreating ? handleCreateTeam : () => handleUpdateTeam(group?.id)}
                                disabled={isSubmitting}
                                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isSubmitting && (
                                    <i className="fa-solid fa-spinner fa-spin"></i>
                                )}
                                {isCreating ? 'Create Group' : 'Update Group'}
                            </button>
                        </div>
                    </div>
                </div>

            </div>

            {/* User Modal */}
            {showUserModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 w-full max-w-2xl shadow-xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-black dark:text-white">Add Team Members</h3>
                            <button 
                                onClick={() => setShowUserModal(false)} 
                                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                aria-label="Close"
                            >
                                <i className="fa-solid fa-times text-xl"></i>
                            </button>
                        </div>
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={userSearch}
                            onChange={e => setUserSearch(e.target.value)}
                            className="w-full mb-3 px-3 py-2 border dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-black dark:text-white"
                        />
                        <div className="max-h-96 overflow-y-auto mb-4">
                            {availableUsers.length === 0 && (
                                <div className="text-neutral-500 text-sm">No users found.</div>
                            )}
                            <div className="grid gap-2">
                                {availableUsers.map(user => {
                                    const dummyMember = users.find(dm => dm.id === user.id);
                                    return (
                                        <label key={user.id} className="flex items-center gap-3 p-3 border dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedUserIds.includes(user.id)}
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        setSelectedUserIds(prev => [...prev, user.id]);
                                                    } else {
                                                        setSelectedUserIds(prev => prev.filter(id => id !== user.id));
                                                    }
                                                }}
                                            />
                                            <div className="flex items-center gap-3 flex-1">
                                                <img
                                                    src={dummyMember?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.name}`}
                                                    className="w-10 h-10 rounded-full border"
                                                    alt={user.name}
                                                />
                                                <div className="flex-1">
                                                    <div className="font-medium text-black dark:text-white">{user.name}</div>
                                                    <div className="text-sm text-neutral-500 dark:text-neutral-400">{user.phone}</div>
                                                    {dummyMember && (
                                                        <div className="text-xs text-neutral-400 dark:text-neutral-500">
                                                            {"dept"} • {"designation"}
                                                        </div>
                                                    )}
                                                </div>
                                                {dummyMember && (
                                                    <div className="text-right">
                                                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                                                            {dummyMember.phone}
                                                        </div>
                                                        {/* <div className="text-xs text-neutral-400 dark:text-neutral-500">
                                                            Joined: {dummyMember.joinDate}
                                                        </div> */}
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowUserModal(false)}
                                className="btn-outline"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleAddSelectedUsers}
                                disabled={selectedUserIds.length === 0}
                                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Add Selected ({selectedUserIds.length})
                            </button>
                        </div>
                    </div>
                </div>
            )}


        </div>

    );
} 